import { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { useEthnicGroups } from "features/patients/hooks/useEthnicGroups";
import { nextOfKinRelationshipOptions } from "utils/patientFormOptions";

import {
  CREATE_DEFAULT_VALUES,
  getEditDefaultValues,
} from "../utils/patientProfileFormDefaults";
import { getPatientProfileFormSchema } from "../utils/patientProfileFormSchema";

export function usePatientProfileForm({ mode, patient, onSubmit }) {
  const isCreate = mode === "create";

  const { data: ethnicGroupsData } = useEthnicGroups();
  const ethnicGroups = useMemo(
    () => ethnicGroupsData || [],
    [ethnicGroupsData]
  );

  const nextOfKinRelationshipSelectOptions = useMemo(() => {
    const stored =
      !isCreate && typeof patient?.nextOfKinRelationship === "string"
        ? patient.nextOfKinRelationship.trim()
        : "";
    if (stored && !nextOfKinRelationshipOptions.includes(stored)) {
      return [...nextOfKinRelationshipOptions, stored];
    }
    return nextOfKinRelationshipOptions;
  }, [isCreate, patient?.nextOfKinRelationship]);

  const schema = useMemo(() => getPatientProfileFormSchema(mode), [mode]);

  const defaultValues = useMemo(
    () => (isCreate ? CREATE_DEFAULT_VALUES : getEditDefaultValues(patient)),
    [isCreate, patient]
  );

  const form = useForm({
    resolver: zodResolver(schema),
    mode: "onSubmit",
    reValidateMode: "onBlur",
    defaultValues,
  });

  useEffect(() => {
    if (!isCreate && patient && typeof patient === "object" && patient.id) {
      form.reset(getEditDefaultValues(patient));
    }
  }, [isCreate, patient, form]);

  const handleFormSubmit = useCallback(
    (data) => {
      if (onSubmit) {
        onSubmit({
          ...data,
          postCode: data.postCode
            ? data.postCode.replace(/\s+/g, "")
            : data.postCode,
        });
      }
    },
    [onSubmit]
  );

  return {
    form,
    isCreate,
    ethnicGroups,
    nextOfKinRelationshipSelectOptions,
    handleFormSubmit,
  };
}
